import { randomUUID } from "replo-utils/lib/crypto";
import { defaultLogger } from "replo-utils/lib/log";
import { isNullish } from "replo-utils/lib/misc";
import { z } from "zod";

import {
  IframeRpcChannelConfigurationError,
  IframeRpcChannelInvalidInputError,
  IframeRpcChannelRemoteError,
  IframeRpcChannelTargetUnavailableError,
  IframeRpcChannelTimeoutError,
  IframeRpcChannelUnknownMethodError,
  IframeRpcChannelUnsubscribedError,
} from "./errors";

export type IframeRpcChannelMethodConfig = Record<
  string,
  {
    inputSchema: z.ZodType;
    outputSchema: z.ZodType;
    timeoutMs?: number;
  }
>;

type MethodInput<
  Config extends IframeRpcChannelMethodConfig,
  Method extends keyof Config,
> = z.input<Config[Method]["inputSchema"]>;

type MethodOutput<
  Config extends IframeRpcChannelMethodConfig,
  Method extends keyof Config,
> = z.output<Config[Method]["outputSchema"]>;

type IsVoidMethod<
  Config extends IframeRpcChannelMethodConfig,
  Method extends keyof Config,
> = MethodOutput<Config, Method> extends void ? true : false;

type CallResult<
  Config extends IframeRpcChannelMethodConfig,
  Method extends keyof Config,
> =
  IsVoidMethod<Config, Method> extends true
    ? void
    : Promise<MethodOutput<Config, Method>>;

// `z.undefined()` methods are called with no args (`remote.notifyReady()`);
// required object inputs stay required.
type MethodArgs<
  Config extends IframeRpcChannelMethodConfig,
  Method extends keyof Config,
> =
  undefined extends MethodInput<Config, Method>
    ? [input?: MethodInput<Config, Method>]
    : [input: MethodInput<Config, Method>];

export type IframeRpcChannelRemote<
  Config extends IframeRpcChannelMethodConfig,
> = {
  [Method in keyof Config]: (
    ...args: MethodArgs<Config, Method>
  ) => CallResult<Config, Method>;
};

export type IframeRpcChannelHandlerMeta<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
> = {
  // Flat outgoing remote so inbound handlers can reply
  // (`meta.channel.setDisplayMode(...)`) without a self-ref to the channel.
  channel: IframeRpcChannelRemote<OutgoingMethods>;
};

export type IframeRpcChannelHandlers<
  IncomingMethods extends IframeRpcChannelMethodConfig,
  OutgoingMethods extends
    IframeRpcChannelMethodConfig = IframeRpcChannelMethodConfig,
> = {
  [Method in keyof IncomingMethods]: (
    input: z.output<IncomingMethods[Method]["inputSchema"]>,
    meta: IframeRpcChannelHandlerMeta<OutgoingMethods>,
  ) => IsVoidMethod<IncomingMethods, Method> extends true
    ? void
    : // Valued handlers stay `unknown` so callers can return domain/generated
      // types that are structurally compatible at runtime; outputSchema.safeParse
      // is the authoritative gate before the response is posted.
      unknown | Promise<unknown>;
};

// Input-only handlers for module-level / React pointer indirection that is
// invoked both from the channel (meta discarded) and from local code paths
// that have no channel (e.g. BedrockProvider replaying getInitialState).
export type IframeRpcChannelInputHandlers<
  IncomingMethods extends IframeRpcChannelMethodConfig,
> = {
  [Method in keyof IncomingMethods]: (
    input: z.output<IncomingMethods[Method]["inputSchema"]>,
  ) => IsVoidMethod<IncomingMethods, Method> extends true
    ? void
    : unknown | Promise<unknown>;
};

const iframeRpcCallResultSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("success"),
    data: z.unknown(),
  }),
  z.object({
    type: z.literal("error"),
    errorMessage: z.string(),
  }),
]);

export const iframeRpcCallSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("notify"),
    method: z.string(),
    data: z.unknown().optional(),
  }),
  z.object({
    type: z.literal("request"),
    id: z.uuid(),
    method: z.string(),
    data: z.unknown().optional(),
  }),
  z.object({
    type: z.literal("response"),
    id: z.uuid(),
    result: iframeRpcCallResultSchema,
  }),
]);

export type IframeRpcCall = z.infer<typeof iframeRpcCallSchema>;

type PendingRequest = {
  method: string;
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

type SubscriberWindow = {
  addEventListener: (
    type: "message",
    listener: (event: MessageEvent) => void,
  ) => void;
  removeEventListener: (
    type: "message",
    listener: (event: MessageEvent) => void,
  ) => void;
};

type TargetIframeWindow = {
  postMessage: (message: unknown, targetOrigin: string) => void;
};

function isFireAndForgetOutputSchema(outputSchema: z.ZodType): boolean {
  // Prefer the Zod 4 type tag so duplicate-bundled zod copies still classify
  // void methods as fire-and-forget; instanceof alone fails across copies.
  return outputSchema.type === "void" || outputSchema instanceof z.ZodVoid;
}

function isUndefinedInputSchema(inputSchema: z.ZodType): boolean {
  return (
    inputSchema.type === "undefined" || inputSchema instanceof z.ZodUndefined
  );
}

// postMessage / JSON often surface an omitted void payload as `null`. Treat
// that as `undefined` only for methods whose input schema is exactly
// `z.undefined()` so nullable object fields stay untouched.
function coerceEmptyRpcInput({
  input,
  inputSchema,
}: {
  input: unknown;
  inputSchema: z.ZodType;
}): unknown {
  if (isNullish(input) && isUndefinedInputSchema(inputSchema)) {
    return undefined;
  }
  return input;
}

type IframeRpcChannelOptions<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
  IncomingMethods extends IframeRpcChannelMethodConfig,
> = {
  subscriberWindow: SubscriberWindow;
  channelIdentifier: string;
  version: number;
  getTargetWindow: () => TargetIframeWindow | null;
  targetOrigin: string;
  isExpectedMessage: (event: MessageEvent) => boolean;
  outgoingMethods: OutgoingMethods;
  incomingMethods: IncomingMethods;
  handlers: IframeRpcChannelHandlers<IncomingMethods, OutgoingMethods>;
};

export type IframeRpcChannel<
  OutgoingMethods extends
    IframeRpcChannelMethodConfig = IframeRpcChannelMethodConfig,
> = {
  subscribe: () => () => void;
  call: <Method extends keyof OutgoingMethods & string>(input: {
    method: Method;
    input: MethodInput<OutgoingMethods, Method>;
  }) => CallResult<OutgoingMethods, Method>;
  outgoingMethods: OutgoingMethods;
};

/**
 * Flat method-per-key facade over `channel.call`. Prefer this at call sites
 * over hand-writing `useMemo(() => ({ foo: (input) => channel.call(...) }))`.
 *
 * Pass a channel directly, or a getter (plus its method config) when the
 * channel is created lazily / may be unavailable (e.g. SSR).
 */
export function createIframeRpcRemote<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
>(
  channel: IframeRpcChannel<OutgoingMethods>,
): IframeRpcChannelRemote<OutgoingMethods>;
export function createIframeRpcRemote<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
>(
  getChannel: () => IframeRpcChannel<OutgoingMethods> | null,
  methods: OutgoingMethods,
): IframeRpcChannelRemote<OutgoingMethods>;
export function createIframeRpcRemote<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
>(
  channelOrGetChannel:
    | IframeRpcChannel<OutgoingMethods>
    | (() => IframeRpcChannel<OutgoingMethods> | null),
  methods?: OutgoingMethods,
): IframeRpcChannelRemote<OutgoingMethods> {
  const getChannel =
    typeof channelOrGetChannel === "function"
      ? channelOrGetChannel
      : () => channelOrGetChannel;

  const outgoingMethods = methods ?? getChannel()?.outgoingMethods ?? null;
  if (!outgoingMethods) {
    throw new IframeRpcChannelConfigurationError({
      message:
        "createIframeRpcRemote requires methods when the channel may be null",
    });
  }

  /* eslint-disable replo/no-as-cast -- NOTE (Noah, 2026-08-10): Object.keys/fromEntries erase the mapped method record; keys are exactly outgoingMethods. Optional arg for z.undefined() methods is validated again inside call(). */
  return Object.fromEntries(
    (Object.keys(outgoingMethods) as (keyof OutgoingMethods & string)[]).map(
      (method) => {
        return [
          method,
          (input?: MethodInput<OutgoingMethods, typeof method>) => {
            const definition = outgoingMethods[method];
            const channel = getChannel();
            if (!channel) {
              if (
                definition &&
                isFireAndForgetOutputSchema(definition.outputSchema)
              ) {
                return undefined;
              }
              return Promise.reject(
                new IframeRpcChannelUnsubscribedError({ method }),
              );
            }
            return channel.call({
              method,
              input: input as MethodInput<OutgoingMethods, typeof method>,
            });
          },
        ];
      },
    ),
  ) as unknown as IframeRpcChannelRemote<OutgoingMethods>;
  /* eslint-enable replo/no-as-cast */
}

export function createIframeRpcChannel<
  OutgoingMethods extends IframeRpcChannelMethodConfig,
  IncomingMethods extends IframeRpcChannelMethodConfig,
>(
  options: IframeRpcChannelOptions<OutgoingMethods, IncomingMethods>,
): IframeRpcChannel<OutgoingMethods> {
  const {
    subscriberWindow,
    channelIdentifier,
    version,
    getTargetWindow,
    targetOrigin,
    isExpectedMessage,
    outgoingMethods,
    incomingMethods,
    handlers,
  } = options;
  const pendingRequests = new Map<string, PendingRequest>();
  let subscriberCount = 0;
  // Assigned after `channel` / remote exist; handlers only run once messages
  // arrive, so this is always set before first invoke.
  let handlerMeta!: IframeRpcChannelHandlerMeta<OutgoingMethods>;

  const postMessage = (
    call: IframeRpcCall,
    targetWindow = getTargetWindow(),
  ) => {
    targetWindow?.postMessage(
      {
        channelIdentifier,
        version,
        ...call,
      },
      targetOrigin,
    );
  };

  const respondWithError = ({
    id,
    errorMessage,
  }: {
    id: string;
    errorMessage: string;
  }) => {
    postMessage({
      type: "response",
      id,
      result: { type: "error", errorMessage },
    });
  };

  const handleRequest = async (
    message: Extract<IframeRpcCall, { type: "request" }>,
  ) => {
    const definition = incomingMethods[message.method];
    if (!definition || isFireAndForgetOutputSchema(definition.outputSchema)) {
      respondWithError({
        id: message.id,
        errorMessage: `Unknown RPC method '${message.method}'`,
      });
      return;
    }

    const parsedInput = definition.inputSchema.safeParse(
      coerceEmptyRpcInput({
        input: message.data,
        inputSchema: definition.inputSchema,
      }),
    );
    if (!parsedInput.success) {
      respondWithError({
        id: message.id,
        errorMessage: `Invalid request payload for '${message.method}': ${parsedInput.error.message}`,
      });
      return;
    }

    try {
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-07-25): The method was checked against incomingMethods and its input schema above; this restores that correlation after dynamic string lookup.
      const handler = handlers[
        // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-07-25): The method existence check above narrows this dynamic wire string to the configured handler keys.
        message.method as keyof IncomingMethods
      ] as (
        input: unknown,
        meta: IframeRpcChannelHandlerMeta<OutgoingMethods>,
      ) => unknown | Promise<unknown>;
      const result = await handler(parsedInput.data, handlerMeta);
      const parsedOutput = definition.outputSchema.safeParse(result);
      if (!parsedOutput.success) {
        respondWithError({
          id: message.id,
          errorMessage: `Invalid response payload for '${message.method}': ${parsedOutput.error.message}`,
        });
        return;
      }
      postMessage({
        type: "response",
        id: message.id,
        result: { type: "success", data: parsedOutput.data },
      });
    } catch (error) {
      respondWithError({
        id: message.id,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleNotification = (
    message: Extract<IframeRpcCall, { type: "notify" }>,
  ) => {
    const definition = incomingMethods[message.method];
    if (!definition || !isFireAndForgetOutputSchema(definition.outputSchema)) {
      return;
    }
    const parsedInput = definition.inputSchema.safeParse(
      coerceEmptyRpcInput({
        input: message.data,
        inputSchema: definition.inputSchema,
      }),
    );
    if (!parsedInput.success) {
      defaultLogger.error(
        `[iframeRpcChannel] Dropping invalid notify for '${message.method}': ${parsedInput.error.message}`,
      );
      return;
    }
    // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-07): Method checked against incomingMethods above; restore correlation after dynamic lookup.
    const handler = handlers[
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-07): Method existence check narrows this wire string to configured handler keys.
      message.method as keyof IncomingMethods
    ] as (
      input: unknown,
      meta: IframeRpcChannelHandlerMeta<OutgoingMethods>,
    ) => void;
    handler(parsedInput.data, handlerMeta);
  };

  const handleResponse = (
    message: Extract<IframeRpcCall, { type: "response" }>,
  ) => {
    const pendingRequest = pendingRequests.get(message.id);
    if (!pendingRequest) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    pendingRequests.delete(message.id);

    if (message.result.type === "error") {
      pendingRequest.reject(
        new IframeRpcChannelRemoteError({
          method: pendingRequest.method,
          message: message.result.errorMessage,
        }),
      );
      return;
    }

    const definition = outgoingMethods[pendingRequest.method];
    const parsedOutput = definition?.outputSchema.safeParse(
      message.result.data,
    );
    if (!parsedOutput?.success) {
      pendingRequest.reject(
        new IframeRpcChannelRemoteError({
          method: pendingRequest.method,
          message: `Invalid response payload: ${
            parsedOutput?.error.message ?? "unknown method"
          }`,
        }),
      );
      return;
    }
    pendingRequest.resolve(parsedOutput.data);
  };

  const handleMessage = (event: MessageEvent) => {
    const parsedMetadata = z
      .object({
        channelIdentifier: z.literal(channelIdentifier),
        version: z.literal(version),
      })
      .safeParse(event.data);
    if (!parsedMetadata.success) {
      return;
    }

    if (!isExpectedMessage(event)) {
      defaultLogger.error("[iframeRpcChannel] Dropping unexpected message", {
        channelIdentifier,
        version,
        origin: event.origin,
        hasSource: Boolean(event.source),
      });
      return;
    }

    const parsedCall = iframeRpcCallSchema.safeParse(event.data);
    if (!parsedCall.success) {
      return;
    }
    const message = parsedCall.data;

    if (message.type === "notify") {
      handleNotification(message);
      return;
    }
    if (message.type === "request") {
      void handleRequest(message);
      return;
    }
    handleResponse(message);
  };

  const rejectPendingRequests = () => {
    for (const pendingRequest of pendingRequests.values()) {
      clearTimeout(pendingRequest.timeoutId);
      pendingRequest.reject(
        new IframeRpcChannelUnsubscribedError({
          method: pendingRequest.method,
        }),
      );
    }
    pendingRequests.clear();
  };

  const subscribe = () => {
    subscriberCount += 1;
    if (subscriberCount === 1) {
      subscriberWindow.addEventListener("message", handleMessage);
    }

    // Per-subscription flag so calling the same unsubscribe twice is a no-op
    // and does not double-decrement subscriberCount.
    let isActive = true;
    return () => {
      if (!isActive) {
        return;
      }
      isActive = false;
      subscriberCount -= 1;
      if (subscriberCount === 0) {
        subscriberWindow.removeEventListener("message", handleMessage);
        rejectPendingRequests();
      }
    };
  };

  const call = <Method extends keyof OutgoingMethods & string>({
    method,
    input,
  }: {
    method: Method;
    input: MethodInput<OutgoingMethods, Method>;
  }): CallResult<OutgoingMethods, Method> => {
    const definition = outgoingMethods[method];
    if (!definition) {
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-08): Unknown methods are not in OutgoingMethods; valued path rejects, void path is unreachable for typed callers.
      return Promise.reject(
        new IframeRpcChannelUnknownMethodError({ method }),
      ) as CallResult<OutgoingMethods, Method>;
    }

    const isFireAndForget = isFireAndForgetOutputSchema(
      definition.outputSchema,
    );
    const parsedInput = definition.inputSchema.safeParse(
      coerceEmptyRpcInput({
        input,
        inputSchema: definition.inputSchema,
      }),
    );
    if (!parsedInput.success) {
      const error = new IframeRpcChannelInvalidInputError({
        method,
        cause: parsedInput.error,
      });
      if (isFireAndForget) {
        // Void callers don't await; throw so invalid payloads surface in the
        // console instead of silently posting nothing.
        throw error;
      }
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-08): Valued methods reject with the channel error.
      return Promise.reject(error) as CallResult<OutgoingMethods, Method>;
    }

    if (isFireAndForget) {
      postMessage({
        type: "notify",
        method,
        data: parsedInput.data,
      });
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-08): Fire-and-forget methods return sync void.
      return undefined as CallResult<OutgoingMethods, Method>;
    }

    if (subscriberCount === 0) {
      // Note (Noah, 2026-08-07): If there are no subscribers, that means that
      // we will never receive a response, thus it makes no sense to make the request
      // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-08): Valued methods reject when unsubscribed.
      return Promise.reject(
        new IframeRpcChannelUnsubscribedError({ method }),
      ) as CallResult<OutgoingMethods, Method>;
    }

    const targetWindow = getTargetWindow();
    if (!targetWindow) {
      // eslint-disable-next-line replo/no-as-cast -- Valued methods reject before registering pending state when the target is gone.
      return Promise.reject(
        new IframeRpcChannelTargetUnavailableError({
          callType: "request",
          method,
        }),
      ) as CallResult<OutgoingMethods, Method>;
    }

    const id = randomUUID();
    const timeoutMs = definition.timeoutMs ?? 30_000;
    return new Promise<MethodOutput<OutgoingMethods, Method>>(
      (resolve, reject) => {
        const timeoutId = setTimeout(() => {
          pendingRequests.delete(id);
          reject(new IframeRpcChannelTimeoutError({ method, timeoutMs }));
        }, timeoutMs);
        pendingRequests.set(id, {
          method,
          // PendingRequest is shared across methods, so resolve is typed as
          // unknown; handleResponse already validated against this method's
          // outputSchema before calling it.
          resolve: (value) => {
            // eslint-disable-next-line replo/no-as-cast -- NOTE (Noah, 2026-08-07): See comment above.
            resolve(value as MethodOutput<OutgoingMethods, Method>);
          },
          reject,
          timeoutId,
        });
        try {
          postMessage(
            {
              type: "request",
              id,
              method,
              data: parsedInput.data,
            },
            targetWindow,
          );
        } catch (error) {
          clearTimeout(timeoutId);
          pendingRequests.delete(id);
          reject(error);
        }
      },
    ) as CallResult<OutgoingMethods, Method>;
  };

  const channel: IframeRpcChannel<OutgoingMethods> = {
    subscribe,
    call,
    outgoingMethods,
  };
  handlerMeta = { channel: createIframeRpcRemote(channel) };
  return channel;
}
